import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BookingStatus, TRIP_TYPE_LABELS, TripType } from '@fleet/shared';
import { bookingsApi, type CalendarEntry, type CalendarVehicle } from '../api/bookings';
import {
  DAY_END_HOUR,
  DAY_START_HOUR,
  GRID_HEIGHT,
  SLOT_HEIGHT,
  blockGeometry,
  dayRange,
  formatDay,
  formatTime,
  hourLabels,
  instantFromSlotIndex,
  slotIndexFromOffset,
} from './calendar-geometry';

interface Props {
  day: Date;
  vehicleGroupId?: string;
  /** Leere Menge heißt: keine Einschränkung. */
  vehicleIds: Set<string>;
  onSelectRange: (vehicle: CalendarVehicle, from: Date, to: Date) => void;
  onSelectBooking: (entry: CalendarEntry) => void;
}

interface DragState {
  vehicleId: string;
  startSlot: number;
  endSlot: number;
}

/**
 * Tages-Ressourcenansicht: Zeit vertikal, Fahrzeuge als Spalten.
 *
 * Diese Anordnung beantwortet die Frage, die tatsächlich gestellt wird — „welches
 * Fahrzeug ist um 14 Uhr frei?" — ohne einen einzigen Klick. Bei sieben
 * Fahrzeugen passen alle nebeneinander; müsste man zwischen Fahrzeugen blättern,
 * ginge genau dieser Vorteil verloren.
 */
export function DayCalendar({
  day,
  vehicleGroupId,
  vehicleIds,
  onSelectRange,
  onSelectBooking,
}: Props) {
  const [drag, setDrag] = useState<DragState | null>(null);

  const { from, to } = useMemo(() => dayRange(day), [day]);

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['availability', from.toISOString(), to.toISOString(), vehicleGroupId ?? null],
    queryFn: () => bookingsApi.availability(from, to, vehicleGroupId),
  });

  const vehicles = (data?.vehicles ?? []).filter(
    (vehicle) => vehicleIds.size === 0 || vehicleIds.has(vehicle.id),
  );

  const entriesByVehicle = useMemo(() => {
    const map = new Map<string, CalendarEntry[]>();
    for (const entry of data?.entries ?? []) {
      const list = map.get(entry.vehicleId) ?? [];
      list.push(entry);
      map.set(entry.vehicleId, list);
    }
    return map;
  }, [data]);

  function handlePointerDown(vehicle: CalendarVehicle, event: React.PointerEvent<HTMLDivElement>) {
    if (!vehicle.canBook) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const slot = slotIndexFromOffset(event.clientY - bounds.top);
    setDrag({ vehicleId: vehicle.id, startSlot: slot, endSlot: slot });
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function handlePointerMove(event: React.PointerEvent<HTMLDivElement>) {
    if (!drag) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    setDrag({ ...drag, endSlot: slotIndexFromOffset(event.clientY - bounds.top) });
  }

  function handlePointerUp(vehicle: CalendarVehicle) {
    if (!drag || drag.vehicleId !== vehicle.id) return;

    const first = Math.min(drag.startSlot, drag.endSlot);
    // Das Ende ist exklusiv: Ein einzelner Klick ergibt eine Viertelstunde.
    const last = Math.max(drag.startSlot, drag.endSlot) + 1;

    setDrag(null);
    onSelectRange(vehicle, instantFromSlotIndex(day, first), instantFromSlotIndex(day, last));
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
        <span className="text-sm font-medium">{formatDay(day)}</span>
        <Legend />
      </header>

      {isError && (
        <p className="px-4 py-6 text-sm text-red-700">
          Belegungen konnten nicht geladen werden: {(error as Error).message}
        </p>
      )}

      {isPending && <p className="px-4 py-6 text-sm text-slate-500">Wird geladen …</p>}

      {data && (
        <div className="overflow-x-auto">
          <div className="flex min-w-max">
            {/* Zeitachse */}
            <div className="sticky left-0 z-10 w-14 shrink-0 bg-white">
              <div className="h-14 border-b border-slate-200" />
              <div className="relative" style={{ height: GRID_HEIGHT }}>
                {hourLabels().map(({ hour, top }) => (
                  <div
                    key={hour}
                    className="absolute -translate-y-1/2 pr-2 text-right text-xs tabular-nums text-slate-400"
                    style={{ top, width: '100%' }}
                  >
                    {String(hour).padStart(2, '0')}:00
                  </div>
                ))}
              </div>
            </div>

            {vehicles.map((vehicle) => (
              <VehicleColumn
                key={vehicle.id}
                vehicle={vehicle}
                entries={entriesByVehicle.get(vehicle.id) ?? []}
                day={day}
                drag={drag?.vehicleId === vehicle.id ? drag : null}
                onPointerDown={(e) => handlePointerDown(vehicle, e)}
                onPointerMove={handlePointerMove}
                onPointerUp={() => handlePointerUp(vehicle)}
                onSelectBooking={onSelectBooking}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function VehicleColumn({
  vehicle,
  entries,
  day,
  drag,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onSelectBooking,
}: {
  vehicle: CalendarVehicle;
  entries: CalendarEntry[];
  day: Date;
  drag: DragState | null;
  onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
  onPointerMove: (event: React.PointerEvent<HTMLDivElement>) => void;
  onPointerUp: () => void;
  onSelectBooking: (entry: CalendarEntry) => void;
}) {
  const dragTop = drag ? Math.min(drag.startSlot, drag.endSlot) * SLOT_HEIGHT : 0;
  const dragHeight = drag ? (Math.abs(drag.endSlot - drag.startSlot) + 1) * SLOT_HEIGHT : 0;

  return (
    <div className="w-40 shrink-0 border-l border-slate-200">
      <div className="flex h-14 flex-col justify-center border-b border-slate-200 px-2">
        <div className="flex items-center gap-1.5">
          <span
            className="size-2 shrink-0 rounded-full"
            style={{ backgroundColor: vehicle.colorHex }}
            aria-hidden
          />
          <span className="truncate text-sm font-medium">{vehicle.licensePlate}</span>
        </div>
        <span className="truncate text-xs text-slate-500">{vehicle.displayName}</span>
      </div>

      <div
        className={[
          'relative',
          vehicle.canBook ? 'cursor-crosshair' : 'cursor-not-allowed bg-slate-50',
        ].join(' ')}
        style={{ height: GRID_HEIGHT }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        title={
          vehicle.canBook
            ? 'Zum Buchen ziehen'
            : `Für die Gruppe „${vehicle.vehicleGroupName}" fehlt dir die Buchungsberechtigung`
        }
      >
        {/* Rasterlinien: Volle Stunde kräftiger als die Viertelstunde. */}
        {Array.from({ length: (DAY_END_HOUR - DAY_START_HOUR) * 4 }, (_, index) => (
          <div
            key={index}
            className={[
              'absolute w-full border-t',
              index % 4 === 0 ? 'border-slate-200' : 'border-slate-100',
            ].join(' ')}
            style={{ top: index * SLOT_HEIGHT }}
          />
        ))}

        {drag && (
          <div
            className="absolute inset-x-1 z-20 rounded border-2 border-dashed border-slate-900 bg-slate-900/10"
            style={{ top: dragTop, height: dragHeight }}
          />
        )}

        {entries.map((entry) => (
          <BookingBlock
            key={entry.id}
            entry={entry}
            day={day}
            onClick={() => onSelectBooking(entry)}
          />
        ))}
      </div>
    </div>
  );
}

function BookingBlock({
  entry,
  day,
  onClick,
}: {
  entry: CalendarEntry;
  day: Date;
  onClick: () => void;
}) {
  const start = new Date(entry.startsAt);
  const end = new Date(entry.endsAt);
  const geometry = blockGeometry(start, end, day);

  const pending = entry.status === BookingStatus.PENDING_APPROVAL;
  const isPrivate = entry.tripType === TripType.PRIVAT;

  return (
    <button
      type="button"
      onClick={onClick}
      // Nicht als Drag-Start werten: Ein Klick auf eine Buchung soll sie öffnen,
      // nicht darüber eine neue anlegen.
      onPointerDown={(event) => event.stopPropagation()}
      className={[
        'absolute inset-x-1 z-10 overflow-hidden rounded px-1.5 py-1 text-left text-xs',
        'border transition-shadow hover:shadow-md',
        pending
          ? 'border-dashed border-amber-400 bg-amber-50 text-amber-900'
          : 'border-transparent bg-slate-800 text-white',
        entry.isOwn && !pending ? 'ring-2 ring-slate-900 ring-offset-1' : '',
      ].join(' ')}
      style={{ top: geometry.top, height: geometry.height }}
      title={[
        `${formatTime(start)}–${formatTime(end)}`,
        entry.driverName ?? 'Belegt',
        TRIP_TYPE_LABELS[entry.tripType],
        entry.publicPurpose,
      ]
        .filter(Boolean)
        .join(' · ')}
    >
      {geometry.clippedTop && <span className="mr-0.5 opacity-70">↑</span>}
      <span className="font-medium tabular-nums">{formatTime(start)}</span>
      {geometry.height > SLOT_HEIGHT * 2 && (
        <div className="truncate opacity-90">
          {/*
            Der Name bleibt stehen, auch bei einer Privatfahrt: Er beantwortet
            die Frage, für die man in den Kalender schaut — bei wem liegt der
            Wagen. Die Fahrtart kommt als Zusatz daneben, sie ersetzt ihn nicht.
          */}
          {entry.driverName ?? 'Belegt'}
          {isPrivate && <span className="opacity-75"> · privat</span>}
        </div>
      )}
      {/*
        Der Kalendertext erst ab vier Vierteln Höhe: Darunter bliebe von ihm
        ohnehin nur ein abgeschnittenes Wort, das mehr Rätsel aufgibt als es
        beantwortet. Vollständig steht er im Tooltip und im Dialog.
      */}
      {entry.publicPurpose && geometry.height > SLOT_HEIGHT * 4 && (
        <div className="truncate opacity-75">{entry.publicPurpose}</div>
      )}
      {geometry.clippedBottom && <span className="opacity-70">↓</span>}
    </button>
  );
}

function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
      <span className="flex items-center gap-1.5">
        <span className="size-3 rounded-sm bg-slate-800" /> freigegeben
      </span>
      <span className="flex items-center gap-1.5">
        <span className="size-3 rounded-sm border border-dashed border-amber-400 bg-amber-50" />{' '}
        wartet auf Freigabe
      </span>
      <span className="flex items-center gap-1.5">
        <span className="size-3 rounded-sm bg-slate-100" /> nicht buchbar
      </span>
    </div>
  );
}
